import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect } from 'react'

import { CredentialsPanel } from '@/components/CredentialsPanel'
import { PageHeader } from '@/components/PageHeader'
import { SubscribeGate } from '@/components/SubscribeGate'
import { Skeleton } from '@/components/ui/skeleton'
import { orgQueries } from '@/lib/org/queries'

export const Route = createFileRoute('/_authenticated/credentials')({
  // `?create=true` (from a "New credential" jump elsewhere) opens the inline
  // create form on arrival.
  validateSearch: (search: Record<string, unknown>): { create?: boolean } => ({
    create: search.create === true || search.create === 'true' || undefined,
  }),
  // Plan status decides between the credentials panel and the subscribe gate, so
  // it is awaited; the credential list itself streams in behind the panel's own
  // loading state.
  loader: async ({ context: { queryClient, activeOrg } }) => {
    if (!activeOrg) return
    // See dashboard.tsx: the component is the error surface, not the route.
    await queryClient
      .ensureQueryData(orgQueries.planStatus(activeOrg.id))
      .catch(() => {})
    // Awaited under SSR only — see dashboard.tsx: an un-awaited prefetch that
    // resolves after render but before dehydration is a hydration mismatch.
    const rest = queryClient.prefetchQuery(orgQueries.credentials(activeOrg.id))
    if (import.meta.env.SSR) await rest
  },
  component: Credentials,
})

function Credentials() {
  const { activeOrg } = Route.useRouteContext()
  const { create } = Route.useSearch()
  const navigate = Route.useNavigate()

  // Consume the one-shot flag: the panel already seeded its create form from
  // it on mount, so strip it from the URL to keep refresh/back clean.
  useEffect(() => {
    if (create) navigate({ search: {}, replace: true })
  }, [create, navigate])

  const planQuery = useQuery(orgQueries.planStatus(activeOrg?.id ?? null))
  const plan = planQuery.data
  const entitled = plan?.entitled ?? false

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Credentials"
        description="Proxy logins your clients connect with."
      />

      {planQuery.isPending ? (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : entitled ? (
        <CredentialsPanel entitled autoCreate={!!create} />
      ) : (
        <SubscribeGate
          plans={plan?.plans ?? []}
          orgId={activeOrg?.id}
          lapsed={plan?.hasSubscription ?? false}
          trialEnded={plan?.trialEnded ?? false}
          canManageBilling={plan?.canManageBilling ?? false}
          accountManaged={plan?.accountManaged ?? false}
        />
      )}
    </div>
  )
}
